import type { HttpTransport } from '@mangovoice/api-client'

import { stringifyParams } from './utils'

export interface AnswerRateRequest {
  start: string
  end: string
  pbx: string
  phone_number?: string | string[]
  tz?: string
  group_by?: string
}

export interface AnswerRateMetricsRequest {
  startDate: string
  endDate: string
  pbxIds: number[]
  phoneNumbers?: string[]
}

export interface AnswerRateTimeseriesRequest {
  startDate: string
  endDate: string
  pbxIds: number[]
  phoneNumbers?: string[]
  timezone?: string
  granularity?: string
}

export interface CallVolumeMetricsRequest {
  startDate: string
  endDate: string
  pbxIds: number[]
  phoneNumbers?: string[]
}

export interface CallVolumeTimeseriesRequest {
  startDate: string
  endDate: string
  pbxIds: number[]
  phoneNumbers?: string[]
  timezone?: string
  granularity?: string
}

export interface VoicemailRateTimeseriesRequest {
  startDate: string
  endDate: string
  pbxIds: number[]
  phoneNumbers?: string[]
  timezone?: string
}

export interface NumberComparisonRequest {
  start: string
  end: string
  pbx: string
  phone_number?: string | string[]
  tz?: string
  group_by?: string
}

export interface ExtensionComparisonRequest {
  start: string
  end: string
  pbx: string
  direction?: string
  extension?: string[]
}

export interface CallsReportRequest {
  [key: string]: unknown
}

export function createReportingResource(transport: HttpTransport) {
  return {
    /** Get answer rate (new flexible API) */
    getAnswerRate: (
      request: AnswerRateRequest,
      config?: { signal?: AbortSignal },
    ) => {
      const qs = stringifyParams({
        start: request.start,
        end: request.end,
        pbx: request.pbx,
        phone_number: request.phone_number,
        tz: request.tz,
        group_by: request.group_by,
      })
      return transport.get<unknown>(
        `/app/v1/reporting/answer-rate/?${qs}`,
        undefined,
        config,
      )
    },

    /** Get number comparison */
    getNumberComparison: (
      request: NumberComparisonRequest,
      config?: { signal?: AbortSignal },
    ) => {
      const qs = stringifyParams({
        start: request.start,
        end: request.end,
        pbx: request.pbx,
        phone_number: request.phone_number,
        tz: request.tz,
        group_by: request.group_by,
      })
      return transport.get<unknown>(
        `/app/v1/reporting/number-comparison/?${qs}`,
        undefined,
        config,
      )
    },

    /** Get extension comparison */
    getExtensionComparison: (
      request: ExtensionComparisonRequest,
      config?: { signal?: AbortSignal },
    ) => {
      const qs = stringifyParams({
        start: request.start,
        end: request.end,
        pbx: request.pbx,
        direction: request.direction,
        extension: request.extension,
      })
      return transport.get<unknown>(
        `/app/v1/reporting/extension-comparison/?${qs}`,
        undefined,
        config,
      )
    },

    /** Get answer rate metrics (aggregate) */
    getAnswerRateMetrics: (
      request: AnswerRateMetricsRequest,
      config?: { signal?: AbortSignal },
    ) => {
      const qs = stringifyParams({
        start: request.startDate,
        end: request.endDate,
        pbxes: request.pbxIds,
        phone_number: request.phoneNumbers,
      })
      return transport.get<unknown>(
        `/app/v1/reporting/metrics/answer-rate/?${qs}`,
        undefined,
        config,
      )
    },

    /** Get answer rate timeseries */
    getAnswerRateTimeseries: (
      request: AnswerRateTimeseriesRequest,
      config?: { signal?: AbortSignal },
    ) => {
      const qs = stringifyParams({
        start: request.startDate,
        end: request.endDate,
        pbxes: request.pbxIds,
        phone_number: request.phoneNumbers,
        tz: request.timezone,
        granularity: request.granularity,
      })
      return transport.get<unknown>(
        `/app/v1/reporting/timeseries/answer-rate/?${qs}`,
        undefined,
        config,
      )
    },

    /** Get call volume metrics (aggregate) */
    getCallVolumeMetrics: (
      request: CallVolumeMetricsRequest,
      config?: { signal?: AbortSignal },
    ) => {
      const qs = stringifyParams({
        start: request.startDate,
        end: request.endDate,
        pbxes: request.pbxIds,
        phone_number: request.phoneNumbers,
      })
      return transport.get<unknown>(
        `/app/v1/reporting/metrics/call-volume/?${qs}`,
        undefined,
        config,
      )
    },

    /** Get call volume timeseries */
    getCallVolumeTimeseries: (
      request: CallVolumeTimeseriesRequest,
      config?: { signal?: AbortSignal },
    ) => {
      const qs = stringifyParams({
        start: request.startDate,
        end: request.endDate,
        pbxes: request.pbxIds,
        phone_number: request.phoneNumbers,
        tz: request.timezone,
        granularity: request.granularity,
      })
      return transport.get<unknown>(
        `/app/v1/reporting/timeseries/call-volume/?${qs}`,
        undefined,
        config,
      )
    },

    /** Get voicemail rate timeseries */
    getVoicemailRateTimeseries: (
      request: VoicemailRateTimeseriesRequest,
      config?: { signal?: AbortSignal },
    ) => {
      const qs = stringifyParams({
        start: request.startDate,
        end: request.endDate,
        pbxes: request.pbxIds,
        phone_number: request.phoneNumbers,
        tz: request.timezone,
      })
      return transport.get<unknown>(
        `/app/v1/reporting/timeseries/voicemail-rate/?${qs}`,
        undefined,
        config,
      )
    },

    /** POST calls report */
    getCallsReport: (
      reportRequest: CallsReportRequest,
      config?: { signal?: AbortSignal },
    ) =>
      transport.post<unknown>(
        '/app/v1/reporting/calls/',
        reportRequest,
        config,
      ),

    /** Get queue dashboard queues */
    getQueueDashboardQueues: (
      pbxId: number,
      config?: { signal?: AbortSignal },
    ) =>
      transport.get<unknown>(
        `/app/v1/extensions/?pbx_id=${pbxId}&type=ExtensionCallQueue`,
        undefined,
        config,
      ),

    /** Get queue dashboard calls/members */
    getQueueDashboardCalls: (
      pbxUuid: string,
      queueNumber: string,
      config?: { signal?: AbortSignal },
    ) =>
      transport.get<unknown>(
        `/app/v1/call-center-queues/members/?pbx_uuid=${pbxUuid}&number=${queueNumber}`,
        undefined,
        config,
      ),

    /** Get queue dashboard agents */
    getQueueDashboardAgents: (
      pbxUuid: string,
      queueNumber: string,
      config?: { signal?: AbortSignal },
    ) =>
      transport.get<unknown>(
        `/app/v1/call-center-queues/agents/?pbx_uuid=${pbxUuid}&number=${queueNumber}`,
        undefined,
        config,
      ),

    /** Get queue dashboard stats (note: uses /api/ prefix, different base URL pattern) */
    getQueueDashboardStats: (
      pbxId: number,
      queueId: string,
      minutes: number,
      config?: { signal?: AbortSignal },
    ) =>
      transport.get<unknown>(
        `/api/pbx/${pbxId}/queues/${queueId}/stats?minutes=${minutes}`,
        undefined,
        config,
      ),

    /** Login an agent to a queue (note: uses /api/ prefix) */
    postQueueAgentLogin: (agentId: string) =>
      transport.post<{ success: boolean }>(`/api/agents/${agentId}/login`),

    /** Logout an agent from a queue (note: uses /api/ prefix) */
    postQueueAgentLogout: (agentId: string) =>
      transport.post<{ success: boolean }>(`/api/agents/${agentId}/logout`),

    /** Pass-through transport for @mangovoice/reporting-ui adapter */
    transport: {
      get: <T>(
        url: string,
        params?: Record<string, unknown>,
        config?: { signal?: AbortSignal },
      ) => transport.get<T>(url, params, config),
      post: <T>(
        url: string,
        data?: unknown,
        config?: { signal?: AbortSignal },
      ) => transport.post<T>(url, data, config),
    },
  }
}
